import wait from 'lib/promise/wait'

const BATCH_KEY_DEFAULT = '__batched:no-params__'

/**
 * Batching is only safe for calls that share the same request params. Some existing production callers pass
 * business/approval context through `params`, so those values are part of the lane key rather than using the first
 * queued call's params for every request in the window.
 */
function batchParamsKey<P>(params: P | undefined): string {
  if (params === undefined) return BATCH_KEY_DEFAULT
  if (typeof params === 'string') return params
  try {
    return JSON.stringify(params)
  } catch {
    return String(params)
  }
}

interface Config<BatchResponse, SingleResponse, SingleArg, AccumulatedArgs extends Record<string, unknown>, Params = undefined> {
  /**
   * Set in milliseconds, defines how much time should be elapsed from the first request
   * waiting for consecutive requests.
   * defaults to 32ms (2 frames at 60 fps worth of time)
   */
  waitTime?: number
  /**
   * This function will be invoked to batch consecutive requests.
   *
   * @param accumulatedArgs previously accumulated single arguments
   * @param singleArg currently passed single argument
   * @returns a batch of single arguments
   */
  argsReducerFn: (accumulatedArgs: AccumulatedArgs, singleArg: SingleArg) => AccumulatedArgs,
  /**
   * This function will be invoked to make the batch request call. Only callers whose `params` map to the
   * same bucket (see partition key helper above) combine into one flush; those calls must share semantics.
   *
   * @param accumulatedArgs accumulated arguments
   * @param params same `params` as the first queued call in this bucket (`undefined` if omitted everywhere)
   * @returns response of the batch request
   */
  batchRequestFn: (accumulatedArgs: AccumulatedArgs, params?: Params) => Promise<BatchResponse>,
  /**
   * This function will be invoked to get a single value from the batch response
   *
   * @param singleArg original single argument
   * @param batchResponse response of the batch request
   * @returns single value from the batch response
   */
  singleValueFn: (singleArg: SingleArg, batchResponse: BatchResponse) => SingleResponse
}

type BatchLane<AccumulatedArgs, BatchResponse> = {
  accumulatedArgs: AccumulatedArgs
  delayedPromise?: Promise<BatchResponse>
}

function createBatchedPromise<
  BatchResponse,
  SingleResponse,
  SingleArg,
  AccumulatedArgs extends Record<string, unknown>,
  Params = undefined,
>(
  config: Config<BatchResponse, SingleResponse, SingleArg, AccumulatedArgs, Params>,
): (singleArg: SingleArg, params?: Params) => Promise<SingleResponse> {
  const {
    waitTime = 32,
    argsReducerFn,
    batchRequestFn,
    singleValueFn,
  } = config

  const batches = new Map<string, BatchLane<AccumulatedArgs, BatchResponse>>()

  function getLane(key: string): BatchLane<AccumulatedArgs, BatchResponse> {
    let lane = batches.get(key)
    if (!lane) {
      lane = { accumulatedArgs: {} as AccumulatedArgs }
      batches.set(key, lane)
    }
    return lane
  }

  return async function(singleArg: SingleArg, params?: Params): Promise<SingleResponse> {
    const key = batchParamsKey(params)
    const lane = getLane(key)
    lane.accumulatedArgs = argsReducerFn(lane.accumulatedArgs, singleArg)

    if (!lane.delayedPromise) {
      const paramsForBatch = params
      lane.delayedPromise = wait(waitTime).then(async() => {
        const batchedArgs = lane.accumulatedArgs
        lane.accumulatedArgs = {} as AccumulatedArgs
        lane.delayedPromise = undefined

        return await batchRequestFn(batchedArgs, paramsForBatch)
      })
    }

    return lane.delayedPromise.then((batchResponse) => singleValueFn(singleArg, batchResponse))
  }
}

// credits for the initial concept go to @zediah
export default createBatchedPromise
